import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Building2,
  MessageSquare,
  ListChecks,
  Route as RouteIcon,
  Archive,
  Settings as SettingsIcon,
  User,
  Sparkles,
  Repeat,
  Share2,
} from "lucide-react";
import {
  CommandDialog,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandSeparator,
} from "@/components/ui/command";
import { agents } from "@/lib/mockData";
import { useStore } from "@/lib/store";

const NAV = [
  { to: "/boardroom", label: "Boardroom", Icon: Building2 },
  { to: "/agents", label: "Agents", Icon: MessageSquare },
  { to: "/tasks", label: "Tasks", Icon: ListChecks },
  { to: "/roadmap", label: "Roadmap", Icon: RouteIcon },
  { to: "/vault", label: "Venture Vault", Icon: Archive },
  { to: "/settings", label: "Settings", Icon: SettingsIcon },
];

export default function CommandPalette() {
  const open = useStore((s) => s.cmdkOpen);
  const setCmdkOpen = useStore((s) => s.setCmdkOpen);
  const closeCmdk = useStore((s) => s.closeCmdk);
  const tasks = useStore((s) => s.tasks);
  const mode = useStore((s) => s.mode);
  const setMode = useStore((s) => s.setMode);
  const navigate = useNavigate();

  // Global keyboard shortcut
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === "k" || e.key === "K")) {
        e.preventDefault();
        setCmdkOpen(!useStore.getState().cmdkOpen);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [setCmdkOpen]);

  const go = (to) => {
    closeCmdk();
    navigate(to);
  };

  const runDebate = () => {
    closeCmdk();
    navigate("/boardroom");
    setTimeout(() => {
      const el = document.getElementById("boardroom-goal-input");
      if (el) el.focus();
    }, 250);
  };

  const toggleMode = () => {
    closeCmdk();
    const next = mode === "bootstrap" ? "funding" : "bootstrap";
    setMode(next);
    toast.success(
      `Switched to ${next === "bootstrap" ? "Bootstrapping" : "Funding"} mode`,
    );
  };

  const shareDataRoom = () => {
    closeCmdk();
    navigate("/vault");
    toast.success("Read-only link copied", {
      description: "Anyone with the link can view this data room.",
    });
  };

  const openTopTasks = tasks.filter((t) => t.status !== "done").slice(0, 5);

  return (
    <CommandDialog
      open={open}
      onOpenChange={setCmdkOpen}
      data-testid="command-palette"
    >
      <CommandInput
        placeholder="Search agents, tasks, navigate..."
        data-testid="cmdk-input"
      />
      <CommandList data-testid="cmdk-list">
        <CommandEmpty>No results found.</CommandEmpty>

        <CommandGroup heading="Navigate">
          {NAV.map(({ to, label, Icon }) => (
            <CommandItem
              key={to}
              data-testid={`cmdk-nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
              value={`navigate ${label}`}
              onSelect={() => go(to)}
            >
              <Icon strokeWidth={2.2} />
              <span>{label}</span>
            </CommandItem>
          ))}
        </CommandGroup>

        <CommandSeparator />

        <CommandGroup heading="Agents">
          {agents.map((a) => (
            <CommandItem
              key={a.id}
              data-testid={`cmdk-agent-${a.id}`}
              value={`agent ${a.name} ${a.role} ${a.focus}`}
              onSelect={() => go(`/agents?id=${a.id}`)}
            >
              <User strokeWidth={2.2} />
              <span>
                {a.name} · {a.role}
              </span>
            </CommandItem>
          ))}
        </CommandGroup>

        <CommandSeparator />

        <CommandGroup heading="Tasks">
          {openTopTasks.map((t) => (
            <CommandItem
              key={t.id}
              data-testid={`cmdk-task-${t.id}`}
              value={`task ${t.title}`}
              onSelect={() => go(`/tasks#task=${t.id}`)}
            >
              <ListChecks strokeWidth={2.2} />
              <span className="truncate">{t.title}</span>
            </CommandItem>
          ))}
        </CommandGroup>

        <CommandSeparator />

        <CommandGroup heading="Quick actions">
          <CommandItem
            data-testid="cmdk-action-run-debate"
            value="run new debate goal"
            onSelect={runDebate}
          >
            <Sparkles strokeWidth={2.2} />
            <span>Run new debate</span>
          </CommandItem>
          <CommandItem
            data-testid="cmdk-action-toggle-mode"
            value="toggle bootstrap funding mode"
            onSelect={toggleMode}
          >
            <Repeat strokeWidth={2.2} />
            <span>Toggle Bootstrap / Funding</span>
          </CommandItem>
          <CommandItem
            data-testid="cmdk-action-share-vault"
            value="share data room vault"
            onSelect={shareDataRoom}
          >
            <Share2 strokeWidth={2.2} />
            <span>Share data room</span>
          </CommandItem>
        </CommandGroup>
      </CommandList>
    </CommandDialog>
  );
}
